import { useEffect, useRef, useState } from 'react';
import { TooltipPosition, getAdjustedPosition } from './utility';

/**
 * Custom hook to manage the visibility of a tooltip with a delay.
 *
 * @param {number} delay - The delay in milliseconds before showing the tooltip.
 * @returns {object} - An object containing the tooltip visibility state and functions to show, hide, and toggle the tooltip.
 */
export const useTooltip = (delay: number, position: TooltipPosition) => {
  const [isVisible, setIsVisible] = useState(false);
  const [computedPosition, setComputedPosition] =
    useState<TooltipPosition>(position);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const tooltipContentRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleResize = () => {
      setComputedPosition(position);

      if (isVisible) {
        adjustPosition();
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [position, isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  const adjustPosition = () => {
    if (tooltipContentRef.current && triggerRef.current) {
      const tooltipRect = tooltipContentRef.current.getBoundingClientRect();
      const triggerRect = triggerRef.current.getBoundingClientRect();

      const adjustedPosition = getAdjustedPosition(
        tooltipRect,
        triggerRect,
        computedPosition
      );
      if (adjustedPosition !== computedPosition) {
        setComputedPosition(adjustedPosition);
      }
    }
  };

  const showTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
      setTimeout(adjustPosition, 0);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  const toggleTooltip = () => {
    setIsVisible(!isVisible);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    isVisible,
    showTooltip,
    hideTooltip,
    toggleTooltip,
    computedPosition,
    tooltipContentRef,
    triggerRef
  };
};
